import {
	IconArrowRight,
	IconBook,
	IconChevronUp,
	IconClock,
	IconEye,
	IconSearch,
} from "@tabler/icons-react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { allPosts } from "content-collections";
import { useMemo, useState } from "react";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { isModuleEnabled } from "@/lib/modules";
import { cn } from "@/lib/utils";
import {
	getStaggeredAnimationDelayStyle,
	MARKETING_GRID_CARD_CLASSES,
	MARKETING_GRID_CARD_OVERLAY_CLASSES,
	MARKETING_SECTION_ANIM_BASE,
} from "@/utils/animation";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_marketing/blog/")({
	beforeLoad: () => {
		if (!isModuleEnabled("marketing_blog")) {
			throw redirect({ to: "/" });
		}
	},
	component: RouteComponent,
	head: () =>
		seo({
			title: "Blog",
			description:
				"Read our latest articles, tutorials, and insights about our product and industry.",
			keywords: "blog, articles, tutorials, insights, product, industry",
		}),
});

type BlogPost = (typeof allPosts)[number];

export function getPublishedPostsSorted(posts: BlogPost[]): BlogPost[] {
	return posts
		.filter((post) => post.published)
		.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function matchesBlogSearch(post: BlogPost, searchTerm: string): boolean {
	if (searchTerm === "") {
		return true;
	}
	const lower = searchTerm.toLowerCase();
	return (
		Boolean(post.title.toLowerCase().includes(lower)) ||
		Boolean(post.summary?.toLowerCase().includes(lower)) ||
		Boolean(post.content?.toLowerCase().includes(lower))
	);
}

export function matchesBlogCategory(
	post: BlogPost,
	selectedCategory: string,
): boolean {
	return selectedCategory === "" || post.category === selectedCategory;
}

export function filterBlogPosts(
	posts: BlogPost[],
	searchTerm: string,
	selectedCategory: string,
): BlogPost[] {
	return posts.filter(
		(post) =>
			matchesBlogSearch(post, searchTerm) &&
			matchesBlogCategory(post, selectedCategory),
	);
}

export function getBlogPostCountLabel(count: number): string {
	return `${count} ${count === 1 ? "post" : "posts"}`;
}

export function getCategoryFilterDisplayLabel(
	selectedCategory: string,
): string {
	if (!selectedCategory) {
		return "All categories";
	}
	return selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1);
}

export function blogPaginationPageVariant(
	currentPage: number,
	page: number,
): "default" | "outline" {
	return currentPage === page ? "default" : "outline";
}

export function blogPaginationPageClassName(isActive: boolean): string {
	return isActive
		? "bg-primary text-primary-foreground shadow-md"
		: "bg-card/50 backdrop-blur-sm border-border/50 hover:bg-card/80";
}

export function paginateBlogPosts<T>(
	posts: T[],
	page: number,
	postsPerPage: number,
): { totalPages: number; currentPosts: T[] } {
	const totalPages = Math.ceil(posts.length / postsPerPage);
	const startIndex = (page - 1) * postsPerPage;
	return {
		totalPages,
		currentPosts: posts.slice(startIndex, startIndex + postsPerPage),
	};
}

export function RouteComponent() {
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategory, setSelectedCategory] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [categoryOpen, setCategoryOpen] = useState(false);
	const postsPerPage = 6; // Increased since we're using 2 columns now

	// Get unique categories from all posts
	const categories = useMemo(() => {
		const cats = allPosts
			.filter((post) => post.published)
			.map((post) => post.category)
			.filter(Boolean);
		return [...new Set(cats)];
	}, []);

	const allPublishedPosts = useMemo(
		() => getPublishedPostsSorted(allPosts),
		[],
	);

	const filteredPosts = useMemo(
		() => filterBlogPosts(allPublishedPosts, searchTerm, selectedCategory),
		[allPublishedPosts, searchTerm, selectedCategory],
	);

	const { totalPages, currentPosts } = paginateBlogPosts(
		filteredPosts,
		currentPage,
		postsPerPage,
	);

	return (
		<MarketingShell>
			<section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
				<MarketingContainer className="pb-8">
					<MarketingHeader
						title="Writing"
						heading="Blog"
						description="Long-form notes on shipping software, running infrastructure, and learning in public."
					/>

					<div className="pb-8">
						{/* Filter and Search Bar */}
						<div
							className={cn("mb-12", MARKETING_SECTION_ANIM_BASE, "delay-200")}
						>
							<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
								<div className="flex items-center gap-3">
									<IconBook className="h-5 w-5 text-primary" />
									<h2 className="text-2xl font-semibold tracking-tight text-foreground">
										Articles
									</h2>
									<Badge variant="outline" className="ml-2">
										{getBlogPostCountLabel(filteredPosts.length)}
									</Badge>
								</div>

								<div className="flex items-center gap-3 w-full sm:w-auto">
									<div className="relative flex-1 sm:flex-none">
										<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
											<IconSearch className="size-4 text-muted-foreground [image-rendering:crisp-edges]" />
										</div>
										<Input
											id="search"
											type="text"
											placeholder="Search posts..."
											value={searchTerm}
											onChange={(e) => {
												setSearchTerm(e.target.value);
												setCurrentPage(1);
											}}
											className="pl-10 w-full sm:w-64 bg-card/50 border-border/50 focus:ring-2 focus:ring-primary/20 !h-10"
										/>
									</div>

									<Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
										<PopoverTrigger
											id="category-filter"
											aria-expanded={categoryOpen}
											className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-input bg-background text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 justify-between bg-card/50 backdrop-blur-sm border-border/50 hover:bg-card/80 transition-all duration-200 w-[140px] sm:w-[160px]"
										>
											<span className="truncate">
												{getCategoryFilterDisplayLabel(selectedCategory)}
											</span>
											<IconChevronUp
												className={cn(
													"h-4 w-4 opacity-50 flex-shrink-0 ml-2 transition-transform duration-500 ease-in-out",
													categoryOpen && "rotate-180",
												)}
											/>
										</PopoverTrigger>
										<PopoverContent className="w-48 p-1 bg-card/95 backdrop-blur-sm border-border/50">
											<div className="space-y-1">
												<Button
													variant="ghost"
													onClick={() => {
														setSelectedCategory("");
														setCurrentPage(1);
														setCategoryOpen(false);
													}}
													className="w-full justify-start px-3 py-2 h-auto text-sm font-normal hover:bg-primary/10"
												>
													All categories
												</Button>
												{categories.map((category) => (
													<Button
														key={category}
														variant={
															category === selectedCategory
																? "secondary"
																: "ghost"
														}
														onClick={() => {
															setSelectedCategory(category);
															setCurrentPage(1);
															setCategoryOpen(false);
														}}
														className="w-full justify-start px-3 py-2 h-auto text-sm font-normal hover:bg-primary/10"
													>
														{category.charAt(0).toUpperCase() +
															category.slice(1)}
													</Button>
												))}
											</div>
										</PopoverContent>
									</Popover>
								</div>
							</div>
						</div>

						{/* Blog Posts Grid */}
						{currentPosts.length > 0 ? (
							<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mb-12">
								{currentPosts.map((post, index) => (
									<Link
										to={"/blog/$path"}
										params={{ path: post._meta.path }}
										className="block h-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										key={post._meta.path}
									>
										<Card
											className={MARKETING_GRID_CARD_CLASSES}
											style={getStaggeredAnimationDelayStyle(index, 0.4)}
										>
											<div className={MARKETING_GRID_CARD_OVERLAY_CLASSES} />

											<CardHeader className="relative flex-1">
												<div className="flex items-start justify-between mb-3">
													{post.category && (
														<Badge variant="secondary" className="text-xs">
															{post.category}
														</Badge>
													)}
													<div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
														<IconEye className="h-4 w-4" />
														{post.readingTime}
													</div>
												</div>

												<CardTitle className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-3 line-clamp-2">
													{post.title}
												</CardTitle>

												{post.summary && (
													<CardDescription className="text-sm leading-relaxed line-clamp-3">
														{post.summary}
													</CardDescription>
												)}
											</CardHeader>

											<CardContent className="relative mt-auto">
												<div className="flex items-center justify-between">
													{post.date && (
														<div className="text-sm text-muted-foreground flex items-center gap-1">
															<IconClock className="h-4 w-4" />
															{new Date(post.date).toLocaleDateString("en-US", {
																year: "numeric",
																month: "short",
																day: "numeric",
															})}
														</div>
													)}
													<div className="flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
														<span className="text-sm font-medium">
															Read more
														</span>
														<IconArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
													</div>
												</div>
											</CardContent>
										</Card>
									</Link>
								))}
							</div>
						) : (
							<div
								className={cn(
									"py-16 text-center",
									MARKETING_SECTION_ANIM_BASE,
									"delay-400",
								)}
							>
								<div className="max-w-md mx-auto">
									<IconBook className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
									{allPublishedPosts.length === 0 ? (
										<>
											<h3 className="text-xl font-semibold text-foreground mb-2">
												No posts yet
											</h3>
											<p className="text-muted-foreground">
												Check back soon for new content!
											</p>
										</>
									) : (
										<>
											<h3 className="text-xl font-semibold text-foreground mb-2">
												No results found
											</h3>
											<p className="text-muted-foreground">
												Try adjusting your search terms or category filter.
											</p>
										</>
									)}
								</div>
							</div>
						)}

						{/* Pagination */}
						{totalPages > 1 && (
							<div
								className={cn(
									"flex items-center justify-center gap-3",
									MARKETING_SECTION_ANIM_BASE,
									"delay-500",
								)}
							>
								<Button
									variant="outline"
									onClick={() =>
										setCurrentPage((prev) => Math.max(prev - 1, 1))
									}
									disabled={currentPage === 1}
									className="bg-card/50 backdrop-blur-sm border-border/50 hover:bg-card/80 transition-all duration-200"
								>
									Previous
								</Button>

								<div className="flex gap-1">
									{Array.from({ length: totalPages }, (_, i) => i + 1).map(
										(page) => (
											<Button
												key={page}
												variant={blogPaginationPageVariant(currentPage, page)}
												onClick={() => setCurrentPage(page)}
												className={`w-10 transition-all duration-200 ${blogPaginationPageClassName(currentPage === page)}`}
											>
												{page}
											</Button>
										),
									)}
								</div>

								<Button
									variant="outline"
									onClick={() =>
										setCurrentPage((prev) => Math.min(prev + 1, totalPages))
									}
									disabled={currentPage === totalPages}
									className="bg-card/50 backdrop-blur-sm border-border/50 hover:bg-card/80 transition-all duration-200"
								>
									Next
								</Button>
							</div>
						)}
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
